import React, { useEffect, useState, useMemo, useRef } from 'react';
import SideNavLayout from '../Sidenav/SideNavLayout';
import api from '../api/api';
import { Backdrop, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToastMessage from '../utils/muitoast';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { calendericonblack } from '../utils/svgs/svgs';
import ViewFormModal from './ViewFormModal';
import WDSFormModal from './WDSformModal';
import WDSMergeFormModal from './wdsmergemodal';
import ViewSHPFormModal from './ViewSHPModal';
import AIFFormModal from './viewAiF';
import ViewTrailerModal from './TrailerForms/ViewTrailerModal';
import { buildDraftJsonData } from './draftTransformers';

const FORM_NAME_MAP = {
  'Accident Incident Investigation Form': 'AIF',
  'Behavior Based Safety Observation Form': 'BBS',
  'Weekly Driver Log Safety Form': 'WDS',
  'Safety Health Primary Audit Form': 'SHP',
  'Trailer Inspection Form': 'TRAILER',
};

const DraftsDashboard = () => {
  const [drafts, setDrafts] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [severity, setSeverity] = useState('success');
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formView, setFormView] = useState(false);
  const [formType, setFormType] = useState('');
  const [rowData, setRowData] = useState(null);
  const navigate = useNavigate();

  // Date range picker
  const [showPicker, setShowPicker] = useState(false);
  const [dateRangeChanged, setDateRangeChanged] = useState(false);
  const pickerRef = useRef(null);
  const [selectionRange, setSelectionRange] = useState([
    {
      startDate: dayjs().startOf('week').add(1, 'day').toDate(),
      endDate: dayjs().endOf('week').toDate(),
      key: 'selection',
    },
  ]);
  const [historyCalendar, setHistoryCalendar] = useState([
    {
      startDate: dayjs().startOf('week').add(1, 'day').toDate(),
      endDate: dayjs().endOf('week').add(1, 'day').toDate(),
      key: 'selection',
    },
  ]);

  // Filter popup
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const filterRef = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState({
    formTypes: [],
    createdBy: [],
  });

  const fetchDrafts = async () => {
    try {
      setLoader(true);
      // The backend paginates drafts/all (max 200/page). Pull every page so the
      // client-side search/filter/pagination below works over the full set.
      const pageSize = 200;
      let page = 1;
      let all = [];
      let total = Infinity;
      // Safety cap to avoid an infinite loop if the API misbehaves.
      while (all.length < total && page <= 50) {
        const response = await api.get('/v1/forms/drafts/all', {
          params: { page, page_size: pageSize },
        });
        // api interceptor unwraps axios response → response IS the data payload
        const batch = response?.drafts || [];
        all = all.concat(batch);
        total = typeof response?.total === 'number' ? response.total : all.length;
        if (batch.length === 0) break;
        page += 1;
      }
      setDrafts(all);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      setDrafts([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  // Close date picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close filter popup on outside click
  useEffect(() => {
    const handleFilterClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterPopup(false);
      }
    };
    document.addEventListener('mousedown', handleFilterClickOutside);
    return () => document.removeEventListener('mousedown', handleFilterClickOutside);
  }, []);

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleDeleteDraft = async (draft) => {
    try {
      setLoader(true);
      const resp = await api.post('/v1/forms/drafts/delete', {
        draft_id: draft.draft_id,
      });
      // api interceptor unwraps axios response → resp IS the data payload
      setSeverity('success');
      setAlertMessage('Draft deleted successfully');
      setOpen(true);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== draft.draft_id));
    } catch (e) {
      console.error('Delete draft error:', e);
      setSeverity('error');
      setAlertMessage('Something went wrong while deleting');
      setOpen(true);
    } finally {
      setLoader(false);
      setDeleteConfirm(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Map a draft object into the shape the view modals expect (same structure
  // as a SafetyForm row returned by /v1/forms/get_by_id). The mobile-app draft
  // form_data is converted into the web json_data shape via the shared
  // transformers in draftTransformers.js so all form types render correctly.
  // ---------------------------------------------------------------------------
  const draftToRowData = (draft) => {
    const formData = draft.form_data || {};
    const ft = FORM_NAME_MAP[draft.form_name] || draft.form_name;
    const firstJob =
      (formData.logEntries || [])[0]?.jobNumber || formData.job_id || '';
    const firstJobName =
      (formData.validatedJobs || []).find((j) => j.job_num === String(firstJob))?.job_name ||
      formData.job_description || formData.jobName || '';

    return {
      id: draft.draft_id,
      emp_id: formData.emp_id ?? null,
      emp_name: formData.emp_name || formData.driver_name || draft.created_by || '',
      job_id: firstJob,
      jobDescription: firstJobName,
      observer_name:
        formData.observer_name || formData.observerName || formData.emp_name || draft.created_by || '',
      filled_date: formData.filled_date || formData.date || formData.auditDate || '',
      form_type: ft,
      companyid: formData.companyid || formData.companyId || draft.company_id || null,
      json_data: buildDraftJsonData(ft, formData),
      documents: formData.documents || '',
      status: draft.status === 'submitted' ? 'A' : 'D',
      method: formData.method || '',
      created_by: draft.created_by || '',
      created_at: draft.created_at || '',
      pm_id: formData.pm_id || null,
      pm_name: formData.pm_name || '',
      pm_status: formData.pm_status || 'N',
      pm_sign: formData.pm_sign || null,
      pm_data: formData.pm_data || null,
      app_version: formData.app_version || '',
      _isDraft: true,
      _pendingImages: formData._pendingImages || null,
    };
  };

  const handleViewDraft = async (draft) => {
    const ft = FORM_NAME_MAP[draft.form_name] || draft.form_name;
    // The list payload (drafts/all) strips inline base64 images to stay light.
    // Fetch the full draft so the view modal renders signatures/images.
    let full = draft;
    try {
      const resp = await api.get('/v1/forms/drafts/get', {
        params: { draft_id: draft.draft_id },
      });
      if (resp?.draft) full = resp.draft;
    } catch (e) {
      console.error('Failed to load full draft for view:', e);
    }
    const mapped = draftToRowData(full);
    setRowData(mapped);
    setFormType(ft);
    setFormView(true);
  };

  const handleEditDraft = (draft) => {
    const ft = FORM_NAME_MAP[draft.form_name] || draft.form_name;
    // Use a dedicated `draft` mode so each edit form loads from the drafts
    // endpoint (draft_id is a UUID, not a numeric SafetyForm rowid).
    const qs = `mode=draft&draft_id=${draft.draft_id}`;
    if (ft === 'BBS') {
      navigate(`/bbs-form?${qs}`);
    } else if (ft === 'SHP' || ft === 'SHPA') {
      navigate(`/safety-form?${qs}`);
    } else if (ft === 'WDS' || ft === 'WDLS') {
      navigate(`/driver-form?${qs}`);
    } else if (ft === 'AIF') {
      navigate(`/accident-form?${qs}`);
    } else if (ft === 'TRAILER') {
      navigate(`/trailer-form?${qs}`);
    }
  };

  // Build unique filter options from current drafts
  const filterOptions = useMemo(() => {
    const formTypes = [...new Set(drafts.map((d) => FORM_NAME_MAP[d.form_name] || d.form_name).filter(Boolean))];
    const createdBy = [...new Set(drafts.map((d) => d.created_by).filter(Boolean))];
    return { formTypes, createdBy };
  }, [drafts]);

  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => {
      const arr = prev[category];
      const updated = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [category]: updated };
    });
  };

  const clearAllFilters = () => {
    setSelectedFilters({ formTypes: [], createdBy: [] });
    setDateRangeChanged(false);
    setSelectionRange([
      {
        startDate: dayjs().startOf('week').add(1, 'day').toDate(),
        endDate: dayjs().endOf('week').toDate(),
        key: 'selection',
      },
    ]);
    setHistoryCalendar([
      {
        startDate: dayjs().startOf('week').add(1, 'day').toDate(),
        endDate: dayjs().endOf('week').add(1, 'day').toDate(),
        key: 'selection',
      },
    ]);
  };

  const activeFilterCount =
    selectedFilters.formTypes.length + selectedFilters.createdBy.length + (dateRangeChanged ? 1 : 0);

  const handleApply = () => {
    setHistoryCalendar(selectionRange);
    setShowPicker(false);
    setDateRangeChanged(true);
  };

  const handleReset = () => {
    const defaultRange = [
      {
        startDate: dayjs().startOf('week').add(1, 'day').toDate(),
        endDate: dayjs().endOf('week').toDate(),
        key: 'selection',
      },
    ];
    setSelectionRange(defaultRange);
    setHistoryCalendar(defaultRange);
    setShowPicker(false);
    setDateRangeChanged(false);
  };

  // Combined filtering: search + date range + filter popup selections
  const filteredDrafts = useMemo(() => {
    let result = drafts;

    // Search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (d) =>
          (d.created_by || '').toLowerCase().includes(term) ||
          (d.form_name || '').toLowerCase().includes(term) ||
          (d.form_title || '').toLowerCase().includes(term) ||
          (d.draft_id || '').toLowerCase().includes(term) ||
          (FORM_NAME_MAP[d.form_name] || '').toLowerCase().includes(term)
      );
    }

    // Date range filter
    if (dateRangeChanged) {
      const start = dayjs(historyCalendar[0].startDate).startOf('day');
      const end = dayjs(historyCalendar[0].endDate).endOf('day');
      result = result.filter((d) => {
        const created = dayjs(d.created_at);
        return created.isValid() && created.isAfter(start) && created.isBefore(end);
      });
    }

    // Form type filter
    if (selectedFilters.formTypes.length > 0) {
      result = result.filter((d) => {
        const shortName = FORM_NAME_MAP[d.form_name] || d.form_name;
        return selectedFilters.formTypes.includes(shortName);
      });
    }

    // Created by filter
    if (selectedFilters.createdBy.length > 0) {
      result = result.filter((d) => selectedFilters.createdBy.includes(d.created_by));
    }

    return result;
  }, [drafts, searchTerm, dateRangeChanged, historyCalendar, selectedFilters]);

  const totalPages = Math.ceil(filteredDrafts.length / rowsPerPage);
  const paginatedData = filteredDrafts.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilters, dateRangeChanged]);

  return (
    <SideNavLayout>
      <div
        className="flex flex-col min-h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] p-4 overflow-y-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/forms')}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
          >
            <ArrowBackIcon style={{ width: 20 }} />
          </button>
          <h1 className="font-inter font-bold text-[24px]">Drafts</h1>
        </div>

        {/* Toolbar */}
        <div className="flex justify-between gap-2 items-center mb-4">
          {/* Search */}
          <div className="relative flex-1 max-w-sm mr-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, form type, title..."
              className="w-full border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black focus:outline-none focus:border-[#8B1818]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            )}
          </div>

          {/* Date Range Picker */}
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setShowPicker(!showPicker)}
              className="flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black"
            >
              {calendericonblack}
              <span>
                {dateRangeChanged
                  ? `${dayjs(historyCalendar[0].startDate).format('MMM D, YYYY')} - ${dayjs(
                      historyCalendar[0].endDate
                    ).format('MMM D, YYYY')}`
                  : 'Select Date'}
              </span>
            </button>

            {showPicker && (
              <div className="absolute right-0 mt-2 z-[9999] shadow-lg border rounded-md bg-white">
                <DateRangePicker
                  onChange={(item) => setSelectionRange([item.selection])}
                  ranges={selectionRange}
                  moveRangeOnFirstSelection={false}
                  maxDate={new Date()}
                />
                <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
                  <button
                    onClick={handleReset}
                    className="px-3 py-1 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleApply}
                    className="px-3 py-1 text-sm rounded-md bg-[#8B1818] text-white hover:bg-[#701313]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filter Button */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              className="flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black hover:bg-gray-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-[#8B1818] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {showFilterPopup && (
              <div className="absolute right-0 mt-2 z-[9999] shadow-xl border border-gray-200 rounded-xl bg-white w-[340px] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                      />
                    </svg>
                    <span className="text-sm font-semibold text-gray-800">Filters</span>
                  </div>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-xs font-medium text-[#8B1818] hover:underline"
                    >
                      Clear All ({activeFilterCount})
                    </button>
                  )}
                </div>

                <div
                  className="max-h-[380px] overflow-y-auto px-5 py-4 space-y-5"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  {/* Form Type */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Form Type
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.formTypes.map((ft) => {
                        const active = selectedFilters.formTypes.includes(ft);
                        return (
                          <button
                            key={ft}
                            onClick={() => toggleFilter('formTypes', ft)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? 'bg-[#8B1818] text-white border-[#8B1818] shadow-sm'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            {ft}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-gray-100" />

                  {/* Created By */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Created By
                    </p>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                      {filterOptions.createdBy.map((name) => {
                        const active = selectedFilters.createdBy.includes(name);
                        return (
                          <label
                            key={name}
                            className={`flex items-center gap-2.5 text-sm cursor-pointer px-3 py-2 rounded-lg transition-all ${
                              active
                                ? 'bg-[#8B1818]/5 border border-[#8B1818]/20'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => toggleFilter('createdBy', name)}
                              className="accent-[#8B1818] w-4 h-4 rounded"
                            />
                            <span
                              className={`text-sm text-left ${
                                active ? 'font-medium text-gray-900' : 'text-gray-600'
                              }`}
                            >
                              {name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setShowFilterPopup(false)}
                    className="px-4 py-1.5 text-sm font-medium rounded-lg bg-[#8B1818] text-white hover:bg-[#701313] transition-colors shadow-sm"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters summary */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {dateRangeChanged && (
              <span className="inline-flex items-center gap-1 bg-[#8B1818]/10 text-[#8B1818] text-xs font-medium px-2.5 py-1 rounded-full border border-[#8B1818]/20">
                {dayjs(historyCalendar[0].startDate).format('MMM D')} -{' '}
                {dayjs(historyCalendar[0].endDate).format('MMM D, YYYY')}
                <button
                  onClick={handleReset}
                  className="ml-1 hover:text-red-700 font-bold"
                >
                  ✕
                </button>
              </span>
            )}
            {selectedFilters.formTypes.map((ft) => (
              <span
                key={ft}
                className="inline-flex items-center gap-1 bg-[#8B1818]/10 text-[#8B1818] text-xs font-medium px-2.5 py-1 rounded-full border border-[#8B1818]/20"
              >
                {ft}
                <button
                  onClick={() => toggleFilter('formTypes', ft)}
                  className="ml-1 hover:text-red-700 font-bold"
                >
                  ✕
                </button>
              </span>
            ))}
            {selectedFilters.createdBy.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 bg-[#8B1818]/10 text-[#8B1818] text-xs font-medium px-2.5 py-1 rounded-full border border-[#8B1818]/20"
              >
                {name}
                <button
                  onClick={() => toggleFilter('createdBy', name)}
                  className="ml-1 hover:text-red-700 font-bold"
                >
                  ✕
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs font-medium text-gray-500 hover:text-[#8B1818] underline ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Results count */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500">
            Showing {paginatedData.length} of {filteredDrafts.length} drafts
            {filteredDrafts.length !== drafts.length && ` (filtered from ${drafts.length} total)`}
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl shadow-md">
          <table className="w-full bg-white text-left rounded-xl">
            <thead className="bg-[#E4E4E7] text-[#6B7280] font-inter text-xs uppercase">
              <tr>
                <th className="px-4 py-3">Created By</th>
                <th className="px-4 py-3">Form Type</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="text-[#374151] text-sm">
              {paginatedData.length > 0 ? (
                paginatedData.map((draft) => (
                  <tr key={draft.draft_id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-4 font-inter font-medium">
                      {draft.created_by || '-'}
                    </td>
                    <td className="px-4 py-4 font-inter text-xs">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-medium">
                        {FORM_NAME_MAP[draft.form_name] || draft.form_name}
                      </span>
                    </td>
                    <td className="px-4 py-4 font-inter">{draft.form_title || '-'}</td>
                    <td className="px-4 py-4 font-inter">
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                          draft.status === 'pending'
                            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                            : draft.status === 'submitted'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-gray-50 text-gray-500 border border-gray-200'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            draft.status === 'pending'
                              ? 'bg-yellow-500'
                              : draft.status === 'submitted'
                              ? 'bg-green-500'
                              : 'bg-gray-400'
                          }`}
                        />
                        {draft.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 font-inter text-xs text-gray-500">
                      {draft.created_at
                        ? dayjs(draft.created_at).format('MM-DD-YYYY hh:mm A')
                        : '-'}
                    </td>
                    <td className="px-4 py-4 font-inter text-xs text-gray-500">
                      {draft.updated_at
                        ? dayjs(draft.updated_at).format('MM-DD-YYYY hh:mm A')
                        : '-'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1">
                        <div
                          onClick={() => handleEditDraft(draft)}
                          title="Edit draft"
                          className="bg-[#1E4976] w-[34px] h-[34px] cursor-pointer text-white flex items-center justify-center rounded hover:bg-blue-700"
                        >
                          <EditIcon style={{ width: '18px' }} />
                        </div>
                        <div
                          onClick={() => handleViewDraft(draft)}
                          title="View draft"
                          className="bg-[#1E4976] w-[34px] h-[34px] cursor-pointer text-white flex items-center justify-center rounded hover:bg-blue-700"
                        >
                          <RemoveRedEyeIcon style={{ width: '18px' }} />
                        </div>
                        <div
                          onClick={() => setDeleteConfirm(draft)}
                          title="Delete draft"
                          className="bg-red-600 w-[34px] h-[34px] cursor-pointer text-white flex items-center justify-center rounded hover:bg-red-700"
                        >
                          <DeleteOutlineIcon style={{ width: '18px' }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                    No drafts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-4 pb-2">
            <div className="flex items-center gap-2 text-sm py-2">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded px-2 py-1"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                className="p-2 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <span className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                className="p-2 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Modals — same as FormsDashboard */}
      {formView && rowData && (
        <>
          {formType === 'BBS' && (
            <ViewFormModal
              formType={formType}
              rowData={rowData}
              setFormView={setFormView}
            />
          )}
          {formType === 'WDS' && (
            rowData?.json_data?.some(obj => obj.Dates) ? (
              <WDSMergeFormModal
                formType={formType}
                rowData={rowData}
                setFormView={setFormView}
              />
            ) : (
              <WDSFormModal
                formType={formType}
                rowData={rowData}
                setFormView={setFormView}
              />
            )
          )}
          {formType === 'WDLS' && (
            <WDSFormModal
              formType={formType}
              rowData={rowData}
              setFormView={setFormView}
            />
          )}
          {(formType === 'SHP' || formType === 'SHPA') && (
            <ViewSHPFormModal
              formType={formType}
              rowData={rowData}
              setFormView={setFormView}
              handleSignOff={() => {}}
            />
          )}
          {formType === 'AIF' && (
            <AIFFormModal
              formType={formType}
              rowData={rowData}
              formView={formView}
              setFormView={setFormView}
            />
          )}
          {formType === 'TRAILER' && (
            <ViewTrailerModal
              formType={formType}
              rowData={rowData}
              setFormView={setFormView}
            />
          )}
        </>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete this draft?</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this{' '}
              {FORM_NAME_MAP[deleteConfirm.form_name] || deleteConfirm.form_name} draft
              {deleteConfirm.created_by ? ` by ${deleteConfirm.created_by}` : ''}?
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-gray-100 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDraft(deleteConfirm)}
                className="px-4 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ToastMessage
        message={alertMessage}
        severity={severity}
        open={open}
        handleClose={handleCloseToast}
      />
    </SideNavLayout>
  );
};

export default DraftsDashboard;
